'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useReducer } from 'react';

import {
  EntryWithID,
  LimitPagination,
  TopicEntry,
} from '@/components/cms/types/entry';
import { SECTION_NAMES } from '@/components/integrations/trackingScripts/constants';

import { BlockWrapper } from '@/features/utils/BlockWrapperDC';

import RelatedArticles from '../relatedArticles';
import { EmptyFeed, FeedResultsHeader } from './EmptyFeed';
import { Filters } from './Filters';
import { searchBlogPosts } from './helpers';
import { Pagination } from './Pagination';
import { Search } from './Search';
import { ARTICLES_PER_PAGE, generateInitialState, reducer } from './state';

type FeedProps = {
  initialArticles?: {
    data: EntryWithID[];
    pagination: LimitPagination;
  };
  allTopics?: {
    data: TopicEntry[];
    pagination: LimitPagination;
  };
  featuredTopics?: TopicEntry[];
  initialTopicSlug?: string;
  paginationLabel: string;
  hasFilters?: boolean;
  hasSearch?: boolean;
};

export function Feed({
  initialArticles,
  allTopics,
  featuredTopics,
  initialTopicSlug,
  paginationLabel,
  hasFilters,
  hasSearch,
}: FeedProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const searchParamsQuery = searchParams.get('query');
  const searchParamsTopic = searchParams.get('topic');

  const [state, dispatch] = useReducer(
    reducer,
    { articles: initialArticles, topic: searchParams.get('topic') },
    generateInitialState
  );

  useEffect(() => {
    const query = searchParamsQuery ?? undefined;
    const topicSlug = initialTopicSlug ?? searchParamsTopic ?? undefined;

    async function filter() {
      dispatch({ type: 'loading_articles' });

      const result = await searchBlogPosts({
        topicSlug,
        query,
        pagination: {
          start: state.offset,
          limit: ARTICLES_PER_PAGE,
        },
      });

      if (!result) {
        dispatch({ type: 'no_articles', payload: { query, topic: topicSlug } });
        return;
      }

      if (query) {
        return dispatch({
          type: 'searched_articles',
          payload: {
            articles: result,
            query,
          },
        });
      }

      dispatch({
        type: 'filtered_articles',
        payload: {
          articles: result,
          topic: topicSlug!,
        },
      });
    }
    filter();
  }, [searchParamsQuery, searchParamsTopic, state.offset, initialTopicSlug]);

  const handleLoadArticles = () => {
    dispatch({ type: 'changed_page' });
  };

  const handleQuerySubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const queryEl = e.currentTarget.elements.namedItem(
      'query'
    ) as HTMLInputElement;
    const query = queryEl?.value;
    const params = new URLSearchParams(searchParams);
    params.delete('topic');
    dispatch({ type: 'reset_page' });
    if (query) {
      params.set('query', query);
    } else {
      params.delete('query');
    }
    router.replace(`${pathname}?${params.toString()}`, {
      scroll: false,
    });
  };

  const handleTopicClick = (topicSlug?: string) => {
    const params = new URLSearchParams(searchParams);
    params.delete('query');
    dispatch({ type: 'reset_page' });
    if (topicSlug && topicSlug !== params.get('topic')) {
      params.set('topic', topicSlug);
    } else {
      params.delete('topic');
    }
    router.replace(`${pathname}?${params.toString()}`, {
      scroll: false,
    });
  };

  return (
    <>
      {(hasFilters || hasSearch) && (
        <BlockWrapper
          classNames={{ root: 'pb-0 md:pb-0 lg:pb-0' }}
          data-tracker-section={SECTION_NAMES.ARTICLES_FEED}
        >
          <div className='grid w-full justify-between gap-8 md:grid-cols-2 md:gap-x-9 md:gap-y-12'>
            {hasFilters ? (
              <Filters
                isFiltering={state.loading && searchParams.has('topic')}
                allTopics={allTopics?.data ?? []}
                featuredTopics={featuredTopics ?? []}
                selectedTopic={searchParamsTopic ?? undefined}
                handleFilterClick={handleTopicClick}
              />
            ) : (
              <div />
            )}
            {hasSearch && (
              <div className='flex-1'>
                <Search
                  isSearching={state.loading && searchParams.has('query')}
                  handleSubmit={handleQuerySubmit}
                  defaultQuery={searchParamsQuery ?? undefined}
                />
              </div>
            )}
          </div>
        </BlockWrapper>
      )}
      {(searchParams.has('query') || searchParams.has('topic')) &&
        state.articles.length === 0 && (
          <EmptyFeed
            query={searchParamsQuery! ?? searchParamsTopic!}
            articles={initialArticles?.data.slice(0, 2) ?? []}
          />
        )}
      {!!state.query && state.articles.length > 0 && (
        <BlockWrapper
          data-tracker-section={SECTION_NAMES.ARTICLES_FEED}
          data-tracker-component={SECTION_NAMES.ARTICLES_FEED}
          classNames={{ root: 'pb-0 pt-8 md:pb-0 lg:pb-0 md:pt-12 lg:pt-12' }}
        >
          <FeedResultsHeader
            count={state.pagination.total}
            query={searchParamsQuery ?? ''}
          />
        </BlockWrapper>
      )}
      {!!state.articles.length && (
        <BlockWrapper
          data-tracker-section={SECTION_NAMES.ARTICLES_FEED}
          data-tracker-component={SECTION_NAMES.ARTICLES_FEED}
          classNames={{
            root: !initialTopicSlug ? 'pt-8 md:pt-12 lg:pt-12' : undefined,
          }}
        >
          <RelatedArticles items={state.articles} />
          {state.pagination.start + state.pagination.limit <
            state.pagination.total && (
            <div className='mx-auto mt-8 w-full md:w-max lg:mt-12'>
              <Pagination
                label={paginationLabel}
                loading={state.loading}
                onLoadMore={handleLoadArticles}
              />
            </div>
          )}
        </BlockWrapper>
      )}
    </>
  );
}
